import { type ThreadIndex } from '@shared/whiteboard/threads'
import { createContext, use } from 'react'
import { readPref, writePref } from '@/lib/storage'

// Board facts derived from the model once per change (threads, what points at
// what), and how this viewer folds threads. Folding is presentation for one
// person (DESIGN.md principle 5): kept in their browser, never board data, so
// it creates no ops, no undo steps, nothing in the AI's diff, and two people
// never fight over it.

/** Thread roots this viewer folded on one board, remembered in the browser. */
export class FoldStore {
  private readonly key: string
  private folded: ReadonlySet<string>
  private readonly listeners = new Set<() => void>()

  constructor(boardId: string) {
    this.key = `folded:${boardId}`
    let ids: string[]
    try {
      const raw = readPref(this.key)
      ids = raw ? (JSON.parse(raw) as string[]) : []
    } catch {
      ids = []
    }
    this.folded = new Set(ids.filter((x) => typeof x === 'string'))
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn)
    return () => {
      this.listeners.delete(fn)
    }
  }

  getSnapshot = () => this.folded

  setFolded(rootId: string, folded: boolean) {
    if (this.folded.has(rootId) === folded) return
    const next = new Set(this.folded)
    if (folded) next.add(rootId)
    else next.delete(rootId)
    this.folded = next
    writePref(this.key, next.size ? JSON.stringify([...next]) : null)
    for (const fn of this.listeners) fn()
  }
}

export type BoardDerived = ThreadIndex & {
  /** Thread roots this viewer folded. */
  folded: ReadonlySet<string>
  /** Replies not drawn because their thread is folded. */
  hidden: ReadonlySet<string>
}

const EMPTY: BoardDerived = {
  threads: new Map(),
  rootOf: new Map(),
  comments: new Map(),
  folded: new Set(),
  hidden: new Set(),
}

export const BoardDerivedContext = createContext<BoardDerived>(EMPTY)

export function useBoardDerived(): BoardDerived {
  return use(BoardDerivedContext)
}
